import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Typography, Slider, FormControlLabel, Checkbox,
  Alert, Box, Divider, CircularProgress, RadioGroup, Radio,
  FormLabel, Tooltip, Grid, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { booksAPI } from '../../services/api';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Available AI models
const AI_MODELS = [
  { value: 'gpt-4.1-2025-04-14', label: 'OpenAI GPT 4.1', provider: 'openai' },
  { value: 'gemini-1.5-pro-001', label: 'Gemini 1.5 Pro', provider: 'vertex' },
  { value: 'gemini-1.5-flash-001', label: 'Gemini 1.5 Flash', provider: 'vertex' },
  { value: 'gemini-2.0-flash-001', label: 'Gemini 2.0 Flash (Recommended)', provider: 'vertex' },
  { value: 'gemini-2.5-pro-exp-03-25', label: 'Gemini 2.5 Pro Exp', provider: 'vertex' },
  { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro Preview', provider: 'vertex' },
  { value: 'gemini-3.1-flash', label: 'Gemini 3.1 Flash', provider: 'vertex' },
  { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro (Recommended)', provider: 'vertex' }
];

// Available AI models for structured generation
const STRUCTURED_MODELS = [
  { value: 'openai', label: 'OpenAI', description: 'Structured output using OpenAI models' },
  { value: 'deepseek', label: 'DeepSeek', description: 'Structured output using DeepSeek models' }
];

// Generation types 
const GENERATION_TYPES = [
  { value: 'standard', label: 'Standard Generation', description: 'Regular text generation without specific structure' },
  { value: 'structured', label: 'Structured Generation', description: 'Generates text with a clear outline and sections' }
];

// Version types and their mapping to minutes
const VERSION_TYPES = [
  { value: 'short', label: 'Short Version'},
  { value: 'medium', label: 'Medium Version'},
  { value: 'long', label: 'Long Version'}
];

// Update the predefined prompts to be in plain text but request Markdown output
const PREDEFINED_PROMPTS = [
  { 
    name: 'Default', 
    value: '' 
  },
  { 
    name: 'Comprehensive Book Summary', 
    value: `Imagine you're a writer who reads books and writes summaries later to be used for narration as audiobooks. 
Generate a {{wordCount}}-word summary of {{title}} by {{author}}.
This {{wordCount}}-word target should be met precisely.
After generating the summary, check the word count and adjust the length to ensure it is at least {{wordCount}} words.

FORMAT REQUIREMENTS:
Your response should be formatted in Markdown with:
- The book title as a level 1 header at the top
- Section headers as level 2 and 3 headers
- Proper emphasis with bold and italic when appropriate
- Bulleted and numbered lists where they make sense
- A well-structured document with clear hierarchy

CONTENT REQUIREMENTS:
- Structure should match the book's content organization
- Number of themes should align with the book structure
- Expand topics and subtopics to meet word count
- Use examples and metaphors to illustrate themes
- Use conversational style with rhetorical questions
- Begin with the book title as a header (# Title) followed by content
- No personal introduction, start directly with the content
- Make it like a proper book summary, but shorter

To reach the target word count:
- Expand on each theme and subtopic
- Provide detailed explanations, examples, and anecdotes
- Include quotes and stories from the book
- Add practical tips and exercises for readers
- Relate concepts to different aspects of life

Remember to output everything in proper Markdown format.`
  },
  {
    name: 'Key Points Summary',
    value: `Create a clear and engaging {{wordCount}}-word summary of "{{title}}" by {{author}}, focusing on the key points and main ideas.

Your response should be formatted in Markdown with:
- Begin with the book title as a level 1 header
- Use level 2 headers for main sections
- Use level 3 headers for subsections if needed
- Use bullet points for listing key concepts
- Use bold text for important terms or ideas
- Use italic text for quotes or emphasis
- Use blockquotes for direct quotes from the book

Structure the summary with a clear introduction, main sections for each major concept, and a conclusion with practical takeaways. Use conversational language that works well for audio narration, while maintaining the {{wordCount}}-word count requirement.

The final document should be formatted in proper Markdown for easy reading.`
  },
  {
    name: 'Executive Brief',
    value: `Create a concise executive brief of {{title}} by {{author}} that captures the essential insights in exactly {{wordCount}} words.

Your output should be formatted in Markdown with:
- Book title as a level 1 header at the top (# Book Title: Executive Brief)
- "Key Takeaways" as a level 2 header
- Bullet points for each main insight
- "Application" as a level 2 header for practical applications
- Bold text for critical concepts and action items

Focus on the most actionable insights from the book, presented in a clear, direct manner suitable for busy professionals. Include specific, practical applications of the main ideas.

The brief should be scannable, precise, and properly formatted in Markdown.`
  }
];

const TextGenerationDialog = ({ 
  open, 
  onClose, 
  bookId, 
  lengthType = 'short', 
  onSuccess, 
  onError,
  defaultGenerationType = 'standard',
  defaultWordCount = 5000,
  defaultStructuredModel = 'openai'
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    lengthType: lengthType,
    language: 'en',
    model: 'gemini-3.1-pro',
    provider: 'vertex',
    temperature: 0.0,
    useSsml: false,
    customPrompt: '',
    useCustomPrompt: false,
    selectedPredefinedPrompt: '',
    // Use the default values from props
    generationType: defaultGenerationType,
    wordCount: defaultWordCount,
    structuredModel: defaultStructuredModel,
    // Add field for Vertex-specific word count
    vertexWordCount: 0, // 0 means not specified
    useVertexWordCount: false // Whether to include in the request
  });
  
  // Reset form when opened with new parameters
  React.useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        lengthType: lengthType,
        generationType: defaultGenerationType,
        wordCount: defaultWordCount,
        structuredModel: defaultStructuredModel
      }));
    }
  }, [open, lengthType, defaultGenerationType, defaultWordCount, defaultStructuredModel]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If changing the model, also update the provider
    if (name === 'model') {
      const selectedModel = AI_MODELS.find(model => model.value === value);
      setFormData({
        ...formData,
        [name]: value,
        provider: selectedModel?.provider || 'vertex'
      });
    } 
    // Handle length type selection
    else if (name === 'lengthType') {
      const selectedType = VERSION_TYPES.find(type => type.value === value);
      setFormData({
        ...formData,
        lengthType: value,
      });
    }
    // Handle predefined prompt selection
    else if (name === 'selectedPredefinedPrompt') {
      const selectedPrompt = PREDEFINED_PROMPTS.find(p => p.name === value);
      setFormData({
        ...formData,
        selectedPredefinedPrompt: value,
        customPrompt: selectedPrompt ? selectedPrompt.value : formData.customPrompt
      });
    }
    // Handle numeric inputs
    else if (name === 'wordCount' || name === 'sectionCount' || name === 'vertexWordCount') {
      const numValue = parseInt(value) || 0;
      setFormData({
        ...formData,
        [name]: numValue
      });
    }
    else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };
  
  const handleTemperatureChange = (_, newValue) => {
    setFormData({
      ...formData,
      temperature: newValue
    });
  };

  const handleStructuredModelChange = (e, newValue) => {
    if (newValue) { // Prevent deselection
      setFormData({
        ...formData,
        structuredModel: newValue
      });
    }
  };
  
  const handleGenerateText = async () => {
    try {
      setLoading(true);
      
      // Use the directly selected length type instead of converting from minutes
      const lengthType = formData.lengthType;
      
      // Construct the request object based on selected provider
      let request;
      let response;
      
      // Only include custom prompt if it's enabled and not empty
      const customPromptToSend = formData.useCustomPrompt && formData.customPrompt ? 
                                 formData.customPrompt : undefined;
      
      console.log('Using custom prompt:', formData.useCustomPrompt, 
                  'Prompt text:', customPromptToSend ? customPromptToSend.substring(0, 50) + '...' : 'none');
      
      // If using structured generation, use the dedicated API endpoint
      if (formData.generationType === 'structured') {
        request = {
          bookId: bookId,
          lengthType: lengthType,
          language: formData.language,
          useSsml: formData.useSsml,
          customPrompt: customPromptToSend,
          wordCount: formData.wordCount,
          sectionCount: formData.sectionCount
        };
        
        try {
          // Choose the appropriate structured generation endpoint based on the selected model
          if (formData.structuredModel === 'deepseek') {
            response = await booksAPI.generateStructuredDeepseekText(request);
          } else {
            // Default to OpenAI
            response = await booksAPI.generateStructuredText(request);
          }
        } catch (error) {
          console.error('Structured text generation not available, falling back to standard:', error);
          // Fall back to OpenAI standard endpoint with the same parameters
          response = await booksAPI.generateText({
            ...request,
            // Include a note in the prompt that this should be structured
            customPrompt: `Please create a structured summary with ${formData.sectionCount} sections. 
                          Target word count: ${formData.wordCount}. 
                          ${customPromptToSend || ''}`
          });
        }
      }
      else if (formData.provider === 'vertex') {
        request = {
          bookId: bookId,
          lengthType: lengthType,
          model: formData.model,
          temperature: formData.temperature,
          language: formData.language,
          useSsml: formData.useSsml,
          customPrompt: customPromptToSend
        };
        
        // Only add wordCount if enabled and value is greater than 0
        if (formData.useVertexWordCount && formData.vertexWordCount > 0) {
          request.wordCount = formData.vertexWordCount;
        }
        
        response = await booksAPI.generateVertexText(request);
      } else {
        request = {
          bookId: bookId,
          lengthType: lengthType,
          language: formData.language,
          useSsml: formData.useSsml,
          customPrompt: customPromptToSend
        };
        
        response = await booksAPI.generateText(request);
      }
      
      // Handle success
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Close dialog
      handleClose();
      
    } catch (error) {
      console.error('Error generating text:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    if (!loading && onClose) {
      onClose();
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Generate Text Summary
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will create a new text summary of the book that can be used for audio generation.
        </Alert>
        
        {/* Version Type Selection */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="version-type-label">Summary Length</InputLabel>
          <Select
            labelId="version-type-label"
            name="lengthType"
            value={formData.lengthType}
            onChange={handleInputChange}
            label="Summary Length"
          >
            {VERSION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Generation Type Selection */}
        <FormControl component="fieldset" margin="normal" fullWidth>
          <FormLabel component="legend">Generation Type</FormLabel>
          <RadioGroup 
            row
            name="generationType"
            value={formData.generationType}
            onChange={handleInputChange}
          >
            {GENERATION_TYPES.map((type) => (
              <FormControlLabel
                key={type.value}
                value={type.value}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{type.label}</Typography>
                    <Tooltip title={type.description}>
                      <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
                    </Tooltip>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
        
        {formData.generationType === 'structured' ? (
          // Structured generation options
          <>
            <Alert severity="success" sx={{ mt: 1, mb: 2 }}>
              Structured generation creates text with a clear outline and well-organized sections,
              making it easier to read and process.
            </Alert>
            
            {/* Add model selection for structured generation */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Structured Generation Model
              </Typography>
              <ToggleButtonGroup
                value={formData.structuredModel}
                exclusive
                onChange={handleStructuredModelChange}
                aria-label="structured generation model"
                size="small"
                fullWidth
              >
                {STRUCTURED_MODELS.map(model => (
                  <ToggleButton key={model.value} value={model.value}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">{model.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {model.description}
                      </Typography>
                    </Box>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Word Count"
                  name="wordCount"
                  type="number"
                  value={formData.wordCount}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 500, max: 5000 }
                  }}
                  helperText="Target word count for the summary (500-5000)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Number of Sections"
                  name="sectionCount"
                  type="number"
                  value={formData.sectionCount}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 3, max: 10 }
                  }}
                  helperText="How many main sections to include (3-10)"
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                labelId="language-label"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                label="Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="fr">French</MenuItem>
                <MenuItem value="de">German</MenuItem>
                <MenuItem value="it">Italian</MenuItem>
                <MenuItem value="pt">Portuguese</MenuItem>
              </Select>
            </FormControl>
          </>
        ) : (
          // Standard generation options
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="model-label">AI Model</InputLabel>
              <Select
                labelId="model-label"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                label="AI Model"
              >
                {AI_MODELS.map(model => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="language-label">Language</InputLabel>
              <Select
                labelId="language-label"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                label="Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="fr">French</MenuItem>
                <MenuItem value="de">German</MenuItem>
                <MenuItem value="it">Italian</MenuItem>
                <MenuItem value="pt">Portuguese</MenuItem>
              </Select>
            </FormControl>
            
            {formData.provider === 'vertex' && (
              <>
                <Box sx={{ mt: 3, mb: 1 }}>
                  <Typography gutterBottom>Temperature: {formData.temperature}</Typography>
                  <Slider
                    value={formData.temperature}
                    onChange={handleTemperatureChange}
                    step={0.05}
                    marks
                    min={0}
                    max={1}
                    valueLabelDisplay="auto"
                    aria-labelledby="temperature-slider"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Lower values produce more focused output.
                    Higher values produce more creative output.
                  </Typography>
                </Box>
                
                {/* Add wordCount control for Vertex */}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="useVertexWordCount"
                      checked={formData.useVertexWordCount}
                      onChange={handleInputChange}
                    />
                  }
                  label="Specify target word count"
                  sx={{ mt: 1, display: 'block' }}
                />
                
                {formData.useVertexWordCount && (
                  <TextField
                    label="Target Word Count"
                    name="vertexWordCount"
                    type="number"
                    value={formData.vertexWordCount}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 0;
                      setFormData({
                        ...formData,
                        vertexWordCount: value
                      });
                    }}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      inputProps: { min: 0, max: 20000 }
                    }}
                    helperText="Target number of words (0-20000, leave at 0 for default length)"
                  />
                )}
              </>
            )}
          </>
        )}
        
        <FormControlLabel
          control={
            <Checkbox
              name="useSsml"
              checked={formData.useSsml}
              onChange={handleInputChange}
            />
          }
          label="Generate with SSML markup (for better audio quality)"
          sx={{ mt: 2, display: 'block' }}
        />
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Advanced Options
        </Typography>
        
        <FormControlLabel
          control={
            <Checkbox
              name="useCustomPrompt"
              checked={formData.useCustomPrompt}
              onChange={handleInputChange}
            />
          }
          label="Use custom generation prompt"
          sx={{ display: 'block', mb: 1 }}
        />
        
        {formData.useCustomPrompt && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="predefined-prompt-label">Predefined Prompts</InputLabel>
              <Select
                labelId="predefined-prompt-label"
                name="selectedPredefinedPrompt"
                value={formData.selectedPredefinedPrompt}
                onChange={handleInputChange}
                label="Predefined Prompts"
              >
                {PREDEFINED_PROMPTS.map((prompt) => (
                  <MenuItem key={prompt.name} value={prompt.name}>
                    {prompt.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select a predefined prompt or customize your own below
              </Typography>
            </FormControl>
            
            <TextField
              label="Custom Prompt"
              name="customPrompt"
              value={formData.customPrompt}
              onChange={handleInputChange}
              multiline
              rows={8}
              fullWidth
              margin="normal"
              helperText="You can use template variables: {{title}}, {{author}}, {{wordCount}}"
            />
            
            <Alert severity="info" sx={{ mt: 1 }}>
              Your custom prompt will be used instead of the default AI instruction.
            </Alert>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleGenerateText} 
          variant="contained" 
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Generating...' : 'Generate Text'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextGenerationDialog;
